import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { soundManager } from '../utils/soundManager';
import { X } from 'lucide-react';

import codexDataStatic from '../data/codex.json';

const LivingCodex = ({ linkTitle = true }) => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const videoRef = useRef(null);

  const codexData = codexDataStatic.filter((entry) => entry.is_published !== false);

  // Play video when section becomes visible
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            video.play().catch(err => console.log('Video autoplay prevented:', err));
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Helper to get the correct image URL
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http') || imageUrl.startsWith('/')) return imageUrl;
    return `/${imageUrl}`;
  };

  const getCategories = () => {
    const categories = ['All'];
    const uniqueCategories = [...new Set(codexData.map(entry => entry.category))];
    return [...categories, ...uniqueCategories];
  };

  const getCategoryCount = (category) => {
    if (category === 'All') return codexData.length;
    return codexData.filter(entry => entry.category === category).length;
  };

  const categories = getCategories();

  const filteredEntries = activeCategory === 'All' 
    ? codexData 
    : codexData.filter(entry => entry.category === activeCategory);

  const handleCategoryClick = (category) => {
    soundManager.play('tab_click');
    setActiveCategory(category);
  };

  const handleEntryClick = (entry) => {
    soundManager.play('modal_reveal');
    setSelectedEntry(entry);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedEntry(null);
    document.body.style.overflow = 'unset';
  };

  return (
    <section 
      id="living-codex" 
      className="relative min-h-screen py-32 px-6 md:px-12 lg:px-24 overflow-hidden"
      style={{ backgroundColor: '#0a0a0a' }}
    >
      {/* Background video */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ 
          opacity: 1,
          filter: 'brightness(1.3) contrast(1.1)'
        }}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        data-testid="codex-background-video"
      >
        <source src="/media/videos/living_codex_background.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      
      {/* Subtle dark overlay for text readability */}
      <div 
        className="absolute inset-0"
        style={{ 
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.25) 100%)',
          zIndex: 1 
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2
            className="text-5xl md:text-6xl lg:text-7xl mb-4"
            style={{
              fontFamily: 'Cinzel, serif',
              color: '#C6A85A',
              textShadow: '0 0 20px rgba(198, 168, 90, 0.3)',
              letterSpacing: '0.05em'
            }}
          >
            {linkTitle ? (
              <Link to="/app/codex" className="transition-colors duration-300 hover:text-[#E0C878]">
                THE LIVING CODEX
              </Link>
            ) : (
              'THE LIVING CODEX'
            )}
          </h2>
          <p 
            className="text-xl md:text-2xl"
            style={{
              fontFamily: 'Cormorant Garamond, serif',
              color: '#C6A85A',
              opacity: 0.8
            }}
          >
            The Names That Shape Destiny
          </p>
          
          {/* Click to Expand indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, y: [0, 8, 0] }}
            transition={{ 
              opacity: { duration: 1, delay: 0.5 },
              y: { duration: 2, repeat: Infinity, ease: "easeInOut" }
            }}
            className="mt-8 flex flex-col items-center"
          >
            <p 
              className="text-sm tracking-widest uppercase mb-2"
              style={{
                fontFamily: 'Cinzel, serif',
                color: '#C6A85A',
                opacity: 0.6
              }}
            >
              Click To Expand
            </p>
            <svg 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="#C6A85A"
              strokeWidth="2"
              strokeLinecap="round" 
              strokeLinejoin="round"
              style={{ opacity: 0.6 }}
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </motion.div>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-4 mb-16 overflow-x-auto pb-4"
        >
          {categories.map(category => {
            const count = getCategoryCount(category);
            if (count === 0) return null;

            return (
              <button
                key={category}
                onClick={() => handleCategoryClick(category)}
                className="px-6 py-3 transition-all duration-300 border rounded-sm"
                style={{
                  fontFamily: 'Cinzel, serif',
                  fontSize: '0.9rem',
                  letterSpacing: '0.05em',
                  backgroundColor: activeCategory === category ? 'rgba(224, 200, 120, 0.15)' : 'transparent',
                  color: activeCategory === category ? '#E0C878' : '#8C6B2E',
                  borderColor: activeCategory === category ? '#E0C878' : '#8C6B2E',
                  boxShadow: activeCategory === category ? '0 0 15px rgba(224, 200, 120, 0.2)' : 'none'
                }}
                data-testid={`filter-${category.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {category}
              </button>
            );
          })}
        </motion.div>

        {/* Codex Grid */}
        <motion.div
          layout
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        >
          <AnimatePresence mode="popLayout">
            {filteredEntries.map((entry, index) => (
              <motion.div
                key={entry.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3, delay: index * 0.02 }}
                onClick={() => handleEntryClick(entry)}
                className="relative group cursor-pointer border border-white/10 rounded-sm overflow-hidden transition-all duration-300 hover:border-[#E0C878] min-h-[200px] flex flex-col"
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  backdropFilter: 'blur(10px)'
                }}
                data-testid={`codex-entry-${entry.id}`}
              >
                {/* Thumbnail if available */}
                {entry.image && (
                  <div className="w-full h-32 overflow-hidden bg-black/30">
                    <img 
                      src={getImageUrl(entry.image)}
                      alt={entry.title}
                      className="w-full h-full transition-transform duration-500 group-hover:scale-110"
                      style={{ objectFit: 'cover', objectPosition: 'top' }}
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        e.target.parentElement.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                
                {/* Entry content */}
                <div className="p-6 flex-1 flex flex-col justify-between">
                  <div 
                    className="text-sm mb-3 tracking-wider uppercase"
                    style={{
                      fontFamily: 'Cinzel, serif',
                      color: '#C6A85A',
                      opacity: 0.8,
                      letterSpacing: '0.1em'
                    }}
                  >
                    {entry.category}
                  </div>
                  <h3 
                    className="text-xl mb-3"
                    style={{
                      fontFamily: 'Cinzel, serif',
                      color: '#E0C878'
                    }}
                  >
                    {entry.title}
                  </h3>
                  <p 
                    className="text-sm line-clamp-3"
                    style={{
                      fontFamily: 'Cormorant Garamond, serif',
                      color: '#C6A85A',
                      opacity: 0.7
                    }}
                  >
                    {entry.description}
                  </p>
                </div>

                {/* Hover glow effect */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  style={{
                    boxShadow: 'inset 0 0 20px rgba(224, 200, 120, 0.15)'
                  }}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {selectedEntry && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
            className="fixed inset-0 z-50 flex items-start justify-center pt-20 pb-20 overflow-y-auto"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.85)',
              backdropFilter: 'blur(0px)'
            }}
            data-testid="codex-modal"
          >
            <motion.div
              initial={{ scale: 0.9, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-4xl mx-4 rounded-sm overflow-hidden border border-[#E0C878]/30"
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.95)',
                boxShadow: '0 0 50px rgba(224, 200, 120, 0.2)'
              }}
            >
              {/* Close button */}
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 z-10 p-2 rounded-full transition-colors"
                style={{
                  backgroundColor: 'rgba(224, 200, 120, 0.1)',
                  color: '#E0C878'
                }}
                data-testid="modal-close-button"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Modal content */}
              <div className="p-6 md:p-8">
                {/* Image with optimized loading - 20% larger */}
                {selectedEntry.image && (
                  <div className="mb-6 rounded-sm overflow-hidden bg-black/50 relative">
                    <img 
                      src={getImageUrl(selectedEntry.image)}
                      alt={selectedEntry.title}
                      className="w-full h-auto max-h-[70vh]"
                      loading="eager"
                      decoding="async"
                      style={{ 
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}

                <div 
                  className="text-sm mb-4 tracking-wider uppercase"
                  style={{
                    fontFamily: 'Cinzel, serif',
                    color: '#C6A85A',
                    opacity: 0.8,
                    letterSpacing: '0.1em'
                  }}
                >
                  {selectedEntry.category}
                </div>

                <h3 
                  className="text-3xl md:text-4xl mb-6"
                  style={{
                    fontFamily: 'Cinzel, serif',
                    color: '#E0C878',
                    textShadow: '0 0 15px rgba(224, 200, 120, 0.3)'
                  }}
                >
                  {selectedEntry.title}
                </h3>

                <p 
                  className="text-lg leading-relaxed"
                  style={{
                    fontFamily: 'Cormorant Garamond, serif',
                    color: '#C6A85A',
                    lineHeight: '1.8'
                  }}
                >
                  {selectedEntry.description}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default LivingCodex;
